import { useState, useEffect, useCallback } from 'react';
import { DEMO_BRAND_ID, isDemoBrand } from '../utils/constants';
import { fetchWithBrand } from '../utils/api';

/**
 * Fetches today's live data on mount for Google Ads, Meta Ads, GA4,
 * IKAS, Shopify, WooCommerce. Checks /api/auth/connections for platform availability.
 */
export function useLiveData(brandId) {
  const [liveGads, setLiveGads] = useState(null);
  const [liveMeta, setLiveMeta] = useState(null);
  const [liveGa4, setLiveGa4] = useState(null);
  const [liveIkas, setLiveIkas] = useState(null);
  const [liveIkasHourly, setLiveIkasHourly] = useState([]);
  const [liveShopify, setLiveShopify] = useState(null);
  const [liveWoo, setLiveWoo] = useState(null);
  const [hasIkasConnected, setHasIkasConnected] = useState(false);
  const [hasShopifyConnected, setHasShopifyConnected] = useState(false);
  const [hasWooConnected, setHasWooConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const fetchLive = useCallback(async () => {
    if (!brandId) return;

    // Demo live data
    if (isDemoBrand(brandId)) {
      setIsLoading(true);
      setLiveGads({
        cost: 12500,
        clicks: 3200,
        impressions: 185000,
        conversions: 145,
        conversion_value: 42500,
        ctr: 1.73,
        cpc: 3.91,
      });
      setLiveMeta({
        spend: 8500,
        clicks: 2100,
        impressions: 125000,
        conversions: 95,
        conversion_value: 28000,
        ctr: 1.68,
        cpc: 4.05,
      });
      setLiveGa4({
        sessions: 8500,
        users: 6200,
        transactions: 185,
        revenue: 62000,
        bounceRate: 42.5,
        avgSessionDuration: 245,
      });
      setUpdatedAt(new Date());
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      // Fetch main 3 platforms in parallel
      const [gadsRes, metaRes, ga4Res] = await Promise.allSettled([
        fetchWithBrand(`/api/brands/${brandId}/live/google-ads/today`, brandId),
        fetchWithBrand(`/api/brands/${brandId}/live/meta-ads/today`, brandId),
        fetchWithBrand(`/api/brands/${brandId}/live/ga4/today`, brandId),
      ]);

      if (gadsRes.status === 'fulfilled' && gadsRes.value.ok) {
        const data = await gadsRes.value.json();
        setLiveGads(data || null);
      } else {
        setLiveGads(null);
      }
      if (metaRes.status === 'fulfilled' && metaRes.value.ok) {
        const data = await metaRes.value.json();
        setLiveMeta(data || null);
      } else {
        setLiveMeta(null);
      }
      if (ga4Res.status === 'fulfilled' && ga4Res.value.ok) {
        const data = await ga4Res.value.json();
        setLiveGa4(data || null);
      } else {
        setLiveGa4(null);
      }

      // Check connections for e-commerce platforms
      try {
        const connRes = await fetchWithBrand('/api/auth/connections', brandId);
        if (connRes.ok) {
          const data = await connRes.json();
          const checkPlatform = (platformName) => {
            if (Array.isArray(data)) {
              return !!data.find(c => String(c.platform || '').toUpperCase() === platformName && String(c.status || '').toUpperCase() === 'ACTIVE');
            } else if (data && typeof data === 'object') {
              const obj = data[platformName] || data[platformName.toLowerCase()];
              return !!(obj && String(obj.status || '').toUpperCase() === 'ACTIVE');
            }
            return false;
          };

          const ikasConn = checkPlatform('IKAS');
          const shopifyConn = checkPlatform('SHOPIFY');
          const wooConn = checkPlatform('WOOCOMMERCE');
          setHasIkasConnected(ikasConn);
          setHasShopifyConnected(shopifyConn);
          setHasWooConnected(wooConn);

          const tzOffset = -new Date().getTimezoneOffset();

          // IKAS live
          if (ikasConn) {
            try {
              const liveRes = await fetchWithBrand(`/api/ikas/live/today?tzOffsetMinutes=${encodeURIComponent(tzOffset)}`, brandId);
              if (liveRes.ok) {
                const liveJs = await liveRes.json().catch(() => ({}));
                const series = Array.isArray(liveJs?.rows) ? liveJs.rows : [];
                setLiveIkasHourly(series);
                let totalsNorm = null;
                if (liveJs?.totals) {
                  const t = liveJs.totals;
                  totalsNorm = {
                    revenue: Number(t.revenue || 0),
                    orders: Number(t.orders || t.orders_count || 0),
                    items: Number(t.items || t.items_count || 0),
                  };
                } else {
                  totalsNorm = series.reduce((acc, r) => {
                    acc.revenue += Number(r.revenue || 0);
                    acc.orders += Number(r.orders || r.orders_count || 0);
                    acc.items += Number(r.items || r.items_count || 0);
                    return acc;
                  }, { revenue: 0, orders: 0, items: 0 });
                }
                setLiveIkas(totalsNorm);
              } else {
                setLiveIkas(null);
                setLiveIkasHourly([]);
              }
            } catch {
              setLiveIkas(null);
              setLiveIkasHourly([]);
            }
          } else {
            setLiveIkas(null);
            setLiveIkasHourly([]);
          }

          // Shopify live
          if (shopifyConn) {
            try {
              const liveRes = await fetchWithBrand(`/api/shopify/live/today?tzOffsetMinutes=${encodeURIComponent(tzOffset)}`, brandId);
              if (liveRes.ok) {
                const js = await liveRes.json().catch(() => ({}));
                const totals = js?.totals || {};
                setLiveShopify({
                  revenue: Number(totals.revenue || 0),
                  orders: Number(totals.orders || totals.orders_count || 0),
                  items: Number(totals.items || totals.items_count || 0),
                });
              } else {
                setLiveShopify(null);
              }
            } catch {
              setLiveShopify(null);
            }
          } else {
            setLiveShopify(null);
          }

          // WooCommerce live
          if (wooConn) {
            try {
              const url = `/api/woocommerce/live/today?source=api&tzOffsetMinutes=${encodeURIComponent(tzOffset)}`;
              const res = await fetchWithBrand(url, brandId);
              if (res.ok) {
                const js = await res.json().catch(() => ({}));
                const t = js?.totals || {};
                setLiveWoo({
                  revenue: Number(t.revenue || 0),
                  orders: Number(t.orders || 0),
                  items: Number(t.items || 0),
                });
              } else {
                setLiveWoo(null);
              }
            } catch {
              setLiveWoo(null);
            }
          } else {
            setLiveWoo(null);
          }
        }
      } catch {
        // Connection check failed, keep defaults
      }
    } catch {
      setLiveGads(null);
      setLiveMeta(null);
      setLiveGa4(null);
      setLiveIkas(null);
    } finally {
      setIsLoading(false);
      setUpdatedAt(new Date());
    }
  }, [brandId]);

  // Fetch on mount
  useEffect(() => {
    fetchLive();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    liveGads,
    liveMeta,
    liveGa4,
    liveIkas,
    liveIkasHourly,
    liveShopify,
    liveWoo,
    hasIkasConnected,
    hasShopifyConnected,
    hasWooConnected,
    isLoading,
    updatedAt,
    refetch: fetchLive,
  };
}
